import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { App } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import type { OpenTable, Reservation } from "../../../api/reservations";
import CloudLinkBanner from "../../../components/CloudLinkBanner";
import type { ModuleOutletContext } from "../../../components/moduleSidebar/ModuleSidebarLayout";
import { useOnReservationsSourceChange } from "../../../hooks/useEdgeMode";
import { useReservationsDay } from "../../../hooks/useReservations";
import { useRestaurantSchema } from "../../../hooks/useRestaurantSchema";
import { useViewport } from "../../../hooks/useViewport";
import { useAppSelector } from "../../../store/hooks";
import { addDays, momentAtMinute } from "../logic/dayTime";
import { scheduleWindowOf, shiftDateOfMoment } from "../logic/workingSlots";
import { newReservationStartFor } from "../logic/newReservation";
import { matchesGuest, matchesTableName } from "../logic/reservationList";
import { deriveReservations } from "../logic/settleClosedBills";
import { canLinkToSchema, canOpenSchemaPage, showsSchemaSetup } from "../logic/schemaSetup";
import { reservationStartMs } from "../logic/tableStatus";
import { useNow } from "../hooks/useNow";
import { isTillUnavailable, useReservationsEdge } from "../hooks/useReservationsEdge";
import ReservationsTopBar from "../components/ReservationsTopBar";
import FloorToolbar from "../components/FloorToolbar";
import DayNavigator from "../components/DayNavigator";
import ReservationRegionTabs from "../components/ReservationRegionTabs";
import RegionGrid from "../components/RegionGrid";
import PageNotice, { SchemaSetupNotice } from "../components/PageNotice";
import LegendItem from "../components/LegendItem";
import { LEGEND_SWATCH, type LegendSwatch } from "../components/legendSwatches";
import ScheduleGrid, { type ScheduleRow } from "./ScheduleGrid";
import ReservationModal, { type ReservationModalTarget } from "./ReservationModal";
import {
  barsOfTable,
  cellStartSlot,
  firstReservationHour,
  hourLoad,
  isInSchedule,
  minuteOfDayIn,
  reservationsOfShift,
  scheduleEndHour,
  scheduleStartHour,
  type ScheduleBar,
  type ScheduleTable,
} from "./scheduleLayout";

const NO_RESERVATIONS: Reservation[] = [];
const NO_OPEN_TABLES: OpenTable[] = [];
// Легендата — общите статуси със същите мостри и надписи като под схемата в
// „Резервации" (Свободна / Резервирана / Заета), плюс двата само оттук.
const LEGEND: { swatch: LegendSwatch; labelKey: string }[] = [
  { swatch: "free", labelKey: "reservations.legend.free" },
  { swatch: "reserved", labelKey: "reservations.legend.reserved" },
  { swatch: "occupied", labelKey: "reservations.legend.occupied" },
  { swatch: "expired", labelKey: "reservationsTable.legend.expired" },
  { swatch: "completed", labelKey: "reservationsTable.legend.completed" },
];

/** Естествено сравнение на имената („2" < „10"). */
const byName = (left: ScheduleTable, right: ScheduleTable) =>
  left.name.localeCompare(right.name, undefined, { numeric: true });

/**
 * „Таблица на резервациите": същата лента и ред (регионите, денят, „Нова
 * резервация") като „Резервации", плюс „Скрий до 1-ва резервация" (крие
 * часовете преди първата активна резервация в региона, за всеки ден). Отдолу
 * търсене по маса/гост (правилата на списъка в „Резервации"), брояч, легенда
 * и решетката маси × часове — само резервациите с лента в деня (сметка без
 * резервация не се показва; настанена от предишен ден не се брои). Клик в
 * свободна половинка час → модал „Нова резервация" за масата и часа; клик на
 * предстояща → модал за редакция; след запис таблицата отива на деня и
 * региона на резервацията. Настаняването е само в „Резервации".
 */
const ReservationsTable = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isMobile, isNarrow } = useViewport();
  const { onOpenMobileMenu } = useOutletContext<ModuleOutletContext>();
  const { isEdgeLocal, locked, dataAsOf } = useReservationsEdge();
  const nowMs = useNow();
  // „Днес" е СМЯНАТА в момента: в 01:30 след петък до 06:00 таблицата е на петък.
  const todayKey = shiftDateOfMoment(nowMs);

  const [pickedDateKey, setPickedDateKey] = useState<string | null>(null);
  const [pickedRegionId, setPickedRegionId] = useState<number | null>(null);
  const [hideBeforeFirst, setHideBeforeFirst] = useState(false);
  const [query, setQuery] = useState("");
  const [modalTarget, setModalTarget] = useState<ReservationModalTarget | null>(null);
  // Току-що записаната резервация — лентата ѝ е оградена и превъртяна в екрана.
  const [revealedId, setRevealedId] = useState<number | null>(null);

  const dateKey = pickedDateKey ?? todayKey;
  const isToday = dateKey === todayKey;
  const schemaQuery = useRestaurantSchema({ live: !isEdgeLocal });
  const regions = useMemo(() => schemaQuery.data ?? [], [schemaQuery.data]);
  const canOpenSchema = useAppSelector((state) => canOpenSchemaPage(state.auth));
  const dayQuery = useReservationsDay(dateKey, { enabled: !locked });
  // Смяна след полунощ: нощта ѝ е в отговора за следващия ден.
  const shiftCrossesMidnight = scheduleWindowOf(dateKey).endHour > 24;
  const nextDayQuery = useReservationsDay(addDays(dateKey, 1), { enabled: !locked && shiftCrossesMidnight });
  // Смяна на деня: докато новият идва, placeholder-ът носи само отворените сметки.
  const isDayPending =
    dayQuery.isPlaceholderData ||
    (shiftCrossesMidnight && (nextDayQuery.isPlaceholderData || nextDayQuery.isLoading));
  const openTables = dayQuery.data?.openTables ?? NO_OPEN_TABLES;
  const ownDayReservations = dayQuery.data?.reservations ?? NO_RESERVATIONS;
  const nextDayReservations = shiftCrossesMidnight
    ? (nextDayQuery.data?.reservations ?? NO_RESERVATIONS)
    : NO_RESERVATIONS;
  const dayReservations = useMemo(
    () => reservationsOfShift(dateKey, ownDayReservations, nextDayReservations),
    [dateKey, ownDayReservations, nextDayReservations],
  );

  const allTables = useMemo(
    () =>
      regions.flatMap((region) =>
        region.elements
          .filter((element) => element.kind === "table")
          .map((table) => ({
            id: table.id,
            name: table.name ?? "",
            seats: table.seats ?? 0,
            regionId: region.id,
          })),
      ),
    [regions],
  );
  // Като в „Резервации": закритите сметки приключват настанените, изтеклите
  // предстоящи са „Неизпълнена", настанените следват преместената си сметка.
  const reservations = useMemo(
    () => deriveReservations(dayReservations, openTables, allTables, nowMs),
    [dayReservations, openTables, allTables, nowMs],
  );

  const region = regions.find((candidate) => candidate.id === pickedRegionId) ?? regions[0];
  const regionTables = allTables.filter((table) => table.regionId === region?.id).sort(byName);
  const regionTableIds = new Set(regionTables.map((table) => table.id));
  const isInRegion = (reservation: Reservation, regionId: number) =>
    allTables.some((table) => table.regionId === regionId && reservation.tableIds.includes(table.id));

  // Прозорецът на деня — работното време: от отварянето до затварянето (или
  // по-широко, ако в деня има резервация преди/след него).
  const nonCancelled = reservations.filter((reservation) => reservation.status !== "cancelled");
  const startMinutes = nonCancelled.map((reservation) => minuteOfDayIn(dateKey, reservationStartMs(reservation)));
  const dayStartHour = scheduleStartHour(dateKey, startMinutes);
  const endHour = scheduleEndHour(dateKey, startMinutes);
  // В броячите влизат само резервациите с лента в разписанието — не и
  // настанена от предишен ден, чиито гости още седят (сървърът я връща и днес).
  const activeReservations = nonCancelled.filter((reservation) => isInSchedule(reservation, dateKey, dayStartHour));
  const regionReservations = activeReservations.filter((reservation) =>
    reservation.tableIds.some((id) => regionTableIds.has(id)),
  );

  // „Скрий до 1-ва резервация" — от часа на първата активна резервация в
  // региона, който се гледа. Няма какво да се скрие (няма активна или първата е
  // в началото на деня) → бутонът липсва, а натиснатият се брои за изключен.
  const firstHour = firstReservationHour(regionReservations, dateKey, dayStartHour);
  const isHidingBeforeFirst = hideBeforeFirst && firstHour !== null;
  const startHour = isHidingBeforeFirst ? firstHour : dayStartHour;
  // Редът остава при търсене по неговата маса (цялото име, като в списъка) или
  // по гост с резервация на нея — „маса 2" не показва и другите маси на групата.
  const hasQuery = query.trim() !== "";
  const rows: ScheduleRow[] = regionTables
    .filter(
      (table) =>
        !hasQuery ||
        matchesTableName(table.name, query) ||
        regionReservations.some(
          (reservation) => reservation.tableIds.includes(table.id) && matchesGuest(reservation, query),
        ),
    )
    .map((table) => ({
      table,
      bars: barsOfTable({ table, reservations, dateKey, startHour, endHour }),
    }));
  const hourLoads = Array.from({ length: endHour - startHour }, (_, index) =>
    hourLoad(regionReservations, dateKey, startHour + index),
  );

  const goToDay = (nextDateKey: string) => {
    setPickedDateKey(nextDateKey === todayKey ? null : nextDateKey);
    setHideBeforeFirst(false);
    setRevealedId(null);
  };
  const pickRegion = (regionId: number) => {
    setPickedRegionId(regionId);
    setRevealedId(null);
  };
  const openNew = () => {
    // Разглеждан друг ден → формата е за него (от началото му), не за днес.
    const startMs = newReservationStartFor({
      isToday,
      dateKey,
      momentMs: isToday ? nowMs : momentAtMinute(dateKey, dayStartHour * 60),
    });
    setRevealedId(null);
    setModalTarget({
      kind: "new",
      seed: startMs === null ? null : { tableId: null, startMs },
    });
  };
  // Часът е първият неминал слот в половинката (19:00 в 19:10 → 19:15) — както формата.
  const handleCellClick = (table: ScheduleTable, minute: number) => {
    setRevealedId(null);
    setModalTarget({
      kind: "new",
      seed: {
        tableId: table.id,
        startMs: momentAtMinute(dateKey, cellStartSlot(dateKey, minute, nowMs) ?? minute),
      },
    });
  };
  const handleBarClick = (bar: ScheduleBar) => {
    if (bar.reservation.status === "upcoming") {
      setRevealedId(null);
      setModalTarget({ kind: "edit", reservation: bar.reservation });
      return;
    }
    message.info(t("reservationsTable.onlyUpcomingEditable"));
  };
  const closeModal = useCallback(() => setModalTarget(null), []);
  // Записаната резервация: таблицата отива на деня и региона ѝ и я огражда
  // (както схемата) — иначе „създадена" за утре или за друг регион е невидима.
  const handleSaved = (saved: Reservation) => {
    setModalTarget(null);
    goToDay(shiftDateOfMoment(reservationStartMs(saved)));
    const savedRegionId = allTables.find((table) => saved.tableIds.includes(table.id))?.regionId;
    if (savedRegionId !== undefined) setPickedRegionId(savedRegionId);
    setQuery("");
    setRevealedId(saved.id);
  };
  // Смяна облак ↔ каса (или заключване без интернет): отворената форма е с
  // данните на другата страна — затваря се, вместо да записва там.
  useOnReservationsSourceChange(closeModal);
  useEffect(() => {
    if (locked) setModalTarget(null);
  }, [locked]);

  const dayNavigator = (
    <DayNavigator dateKey={dateKey} todayKey={todayKey} compact={isNarrow} fill={isNarrow} onChange={goToDay} />
  );
  const hideButton =
    firstHour !== null ? (
      <button
        type="button"
        onClick={() => setHideBeforeFirst((current) => !current)}
        aria-pressed={isHidingBeforeFirst}
        title={isHidingBeforeFirst ? undefined : t("reservationsTable.hideUntilHint")}
        className="h-9 w-full cursor-pointer whitespace-nowrap rounded-[10px] border border-shade-3 bg-surface px-3.5 text-[13px] font-semibold text-ink-2 transition-colors duration-150 hover:bg-fill"
      >
        {isHidingBeforeFirst ? t("reservationsTable.showWholeDay") : t("reservationsTable.hideUntil")}
      </button>
    ) : null;
  const withDayOnly = (notice: ReactNode) => (
    <>
      <FloorToolbar regionTabs={null} dayNavigator={dayNavigator} onNewReservation={null} isNarrow={isNarrow} />
      {notice}
    </>
  );

  const renderBody = () => {
    if (locked) {
      return <PageNotice tone="offline" title={t("edge.unavailableOffline")} hint={t("reservations.offlineHint")} />;
    }
    if (isEdgeLocal && (isTillUnavailable(schemaQuery.error) || isTillUnavailable(dayQuery.error))) {
      return withDayOnly(
        <PageNotice tone="offline" title={t("edge.unavailableOffline")} hint={t("reservations.offlineStaleHint")} />,
      );
    }
    if (schemaQuery.isLoading || (dayQuery.isLoading && !dayQuery.data))
      return withDayOnly(<PageNotice tone="loading" />);
    if ((schemaQuery.isError && !schemaQuery.data) || (dayQuery.isError && !dayQuery.data)) {
      return withDayOnly(
        <PageNotice tone="error" title={t("reservations.loadError")} hint={t("reservations.loadErrorHint")} />,
      );
    }
    // Без нито една маса и с право за схемата — пътят към нея вместо празната
    // таблица (без правото екранът е както досега).
    if (showsSchemaSetup({ regions: schemaQuery.data, canOpenSchema, isEdgeLocal })) {
      return <SchemaSetupNotice />;
    }
    if (!region) {
      return withDayOnly(
        <PageNotice
          tone="empty"
          title={t("reservations.noSchema.title")}
          hint={t("reservations.noSchema.description")}
        />,
      );
    }

    return (
      <>
        <FloorToolbar
          regionTabs={
            isMobile ? (
              <RegionGrid
                regions={regions}
                activeId={region.id}
                onChange={(regionId) => regionId !== null && pickRegion(regionId)}
                captionOf={(item) =>
                  t("reservations.counts.reservations", {
                    count: activeReservations.filter((reservation) => isInRegion(reservation, item.id)).length,
                  })
                }
              />
            ) : (
              <ReservationRegionTabs regions={regions} activeId={region.id} onChange={pickRegion} />
            )
          }
          dayNavigator={dayNavigator}
          onNewReservation={openNew}
          isNarrow={isNarrow}
          extraControl={hideButton}
        />

        <div className="flex min-h-[320px] flex-1 flex-col overflow-hidden rounded-[14px] border border-shade-2 bg-surface">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 border-b border-shade-2 px-4 py-2.5">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3.5 gap-y-2">
              <label
                className={`flex h-9 items-center gap-2 rounded-[10px] border border-shade-3 bg-surface px-3 focus-within:border-brand ${isNarrow ? "w-full" : "w-[240px]"}`}
              >
                <SearchOutlined className="text-[13px] text-muted" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("reservationsTable.search")}
                  aria-label={t("reservationsTable.search")}
                  className={`w-full border-none bg-transparent font-medium text-ink outline-none placeholder:text-faint ${isNarrow ? "text-[16px]" : "text-[13px]"}`}
                />
              </label>
              <span className="text-[13px] tabular-nums text-body">
                {isDayPending ? (
                  t("common.loading")
                ) : (
                  <>
                    <span className="font-bold text-brand-deep-ink">
                      {t("reservations.counts.reservations", {
                        count: regionReservations.length,
                      })}
                    </span>
                    {" · "}
                    {t("reservations.counts.guests", {
                      count: regionReservations.reduce((sum, reservation) => sum + reservation.guests, 0),
                    })}
                  </>
                )}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {LEGEND.map(({ swatch, labelKey }) => (
                <LegendItem key={swatch} swatch={<span className={LEGEND_SWATCH[swatch]} />}>
                  {t(labelKey)}
                </LegendItem>
              ))}
            </div>
          </div>

          {rows.length === 0 && regionTables.length === 0 && canLinkToSchema({ canOpenSchema, isEdgeLocal }) ? (
            // Район без маси — пътят към схемата (както в платното на „Резервации").
            <SchemaSetupNotice scope="region" regionId={region?.id} />
          ) : rows.length === 0 ? (
            <div className="px-6 py-7 text-[12.5px] text-muted">
              {regionTables.length === 0 ? t("reservations.emptyRegion") : t("reservationsTable.noMatch")}
            </div>
          ) : (
            <ScheduleGrid
              rows={rows}
              startHour={startHour}
              endHour={endHour}
              hourLoads={hourLoads}
              dateKey={dateKey}
              nowMs={nowMs}
              showNow={isToday}
              isNarrow={isNarrow}
              isPending={isDayPending}
              highlightId={revealedId}
              onCellClick={handleCellClick}
              onBarClick={handleBarClick}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-canvas">
      <ReservationsTopBar isNarrow={isNarrow} title={t("reservationsTable.title")} onOpenMobileMenu={onOpenMobileMenu} />
      {/* Срещу касата лентата носи и „· данни към 14:32" — кога касата е видяла облака. */}
      {isEdgeLocal && <CloudLinkBanner detail={dataAsOf} />}
      <div
        // Решетката скролва в картата; страницата — само ако редът над нея не се събира (телефон).
        className={`flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto ${isNarrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-6 pt-4"}`}
      >
        {renderBody()}
      </div>
      <ReservationModal
        target={modalTarget}
        regions={regions}
        defaultRegionId={region?.id ?? null}
        enabled={!locked}
        onClose={closeModal}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default ReservationsTable;
